import { getUsername } from "@utils/utilities"
import React, { useEffect, useMemo, useState } from "react"
import { ReactComponent as PennyIcon } from "../../assets/icons/penny_icon.svg"
import type { User } from "firebase/auth"
import BarStepsProgress from "@components/Progress/BarStepsProgress"
import { ReactComponent as ZurichVerticalLogo } from "@assets/icons/zurich_vertical.svg"
import Tags from "@components/Tags/Tags"
import ModalApplyPension from "@components/Modals/ModalApplyPension"
import { Link, useNavigate } from "react-router-dom"

interface UserDataType {
  userData: {
    recommend_zurich_standard?: boolean
    recommend_zurich_nonstandard?: boolean
    recommend_zurich_bestmatch?: string
    first_name?: string
    last_name?: string
    [key: string]: any
  }
  userAuth: User | null | undefined
}

interface RecommendationType {
  title: string
  short_description: string
  description: string
  bestMatch: boolean
  link: string
  type: "zurich_standard_prsa" | "zurich_nonstandard_prsa"
}

type DashboardRecommedationsProps = {
  userData: UserDataType
}

const DashboardRecommedations: React.FC<DashboardRecommedationsProps> = ({ userData }) => {
  const navigate = useNavigate()

  const username = useMemo(() => {
    return getUsername(userData.userAuth, userData.userData, true)
  }, [userData.userAuth, userData.userData])

  const [isApplied, setIsApplied] = useState(false)
  const [appliesOn, setAppliesOn] = useState("")

  const [recommendations, setRecommendations] = useState<RecommendationType[]>([])

  const handleApply = (pensionType: "zurich_standard_prsa" | "zurich_nonstandard_prsa") => {
    setAppliesOn(pensionType)
    setIsApplied(true)
  }

  useEffect(() => {
    const newRecommendations: RecommendationType[] = []
    if (userData?.userData?.recommend_zurich_standard) {
      newRecommendations.push({
        title: "Zurich Standard PRSA",
        short_description: "For those looking for simplicity and flexibility",
        description: "This product is suitable for employees, people who are self-employed and for those not working",
        bestMatch: userData?.userData?.recommend_zurich_bestmatch === "zurich_standard_prsa",
        link: "recommendations/details/zurich_standard",
        type: "zurich_standard_prsa",
      })
    }
    if (userData?.userData?.recommend_zurich_nonstandard) {
      newRecommendations.push({
        title: "Zurich Non-standard PRSA",
        short_description: "For experienced investors",
        description: "This product is suitable for employees, people who are self-employed and for those not working.",
        bestMatch: userData?.userData?.recommend_zurich_bestmatch === "zurich_nonstandard_prsa",
        link: "recommendations/details/zurich_nonstandard",
        type: "zurich_nonstandard_prsa",
      })
    }
    setRecommendations(newRecommendations)
  }, [
    userData?.userData?.recommend_zurich_nonstandard,
    userData?.userData?.recommend_zurich_standard,
    userData?.userData?.recommend_zurich_bestmatch,
  ])

  return (
    <>
      <div className="px-4 pb-6 lg:px-[80px] md:py-10 bg-pn_paper-3 flex flex-col gap-y-0 md:gap-y-6">
        <div className="p-4 md:p-0">
          <BarStepsProgress activeStep={2} />
        </div>

        <div className="p-6 flex flex-col gap-y-0 lg:gap-y-6 border border-pn_gray-200 rounded-2xl bg-pn_white">
          <div className="flex-col pb-6 lg:pb-0 lg:flex-row flex items-start gap-y-6 md:gap-y-0 md:items-center gap-x-6">
            <div className="bg-[#F4E34E] flex items-center justify-center w-[60px] h-[60px] rounded-full">
              <PennyIcon className="w-[24px] h-[27px]" />
            </div>
            <div className="flex flex-col gap-y-1">
              <span className="text-pn-body-large-emphasis text-pn_gray-950">Penny Recommends</span>
              <span className="text-pn-body-small-regular  text-pn_gray-600">
                {recommendations.length > 1
                  ? `${username}, we designed two products that we feel align
                perfectly with the information you shared`
                  : `${username}, we designed a product that we feel aligns perfectly with the information you shared`}
              </span>
            </div>
          </div>

          <div className="flex-col lg:flex-row flex items-stretch gap-x-6">
            {recommendations.map((recommendation, index) => (
              <React.Fragment key={recommendation.type}>
                <div className="flex lg:hidden items-center gap-x-4 py-2 px-6">
                  <div className="divider-dotted-gray flex-1"></div>
                  <span className="text-pn-caption-regular text-pn_gray-600">OPTION {index + 1}</span>
                  <div className="divider-dotted-gray flex-1"></div>
                </div>
                <div className="lg:border-pn_gray-200 lg:border lg:rounded-2xl bg-pn_white px-0 lg:px-6 py-6 flex flex-col gap-y-4 lg:gap-y-6">
                  <div className="flex flex-col gap-y-4">
                    <div className="flex items-center gap-x-6">
                      <div className="rounded-full w-[64px] h-[64px] border border-pn_gray-200 bg-pn_white flex items-center justify-center">
                        <ZurichVerticalLogo />
                      </div>
                      <div className="flex flex-col gap-y-1.5 flex-1">
                        <div className="flex-col lg:flex-row flex items-start gap-y-[6px] lg:gap-y-0 lg:items-center gap-x-4">
                          <span className="text-pn-body-large-emphasis md:text-pn-headline-emphasis flex-1">
                            {recommendation.title}
                          </span>
                          {recommendation.bestMatch && (
                            <div className="w-fit">
                              <Tags label="Best Match" variant="success" />
                            </div>
                          )}
                        </div>

                        <div>
                          <span className="text-pn-body-large-regular hidden bg-pn_yellow-200 lg:inline text-pn_gray-950">
                            {recommendation.short_description}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <span className="inline lg:hidden bg-pn_yellow-200 text-pn-body-large-regular text-pn_gray-950">
                        {recommendation.short_description}
                      </span>
                    </div>

                    <span className="text-pn-body-small-regular md:text-pn-body-large-regular text-pn_gray-600">
                      {recommendation.description}
                    </span>
                  </div>

                  <div className="flex items-center gap-x-4">
                    <button
                      onClick={() => handleApply(recommendation.type)}
                      className="btn-primary-sm-40 flex-1 md:flex-initial"
                    >
                      Apply
                    </button>
                    <Link to={recommendation.link} className="btn-tertiary-sm flex-1 md:flex-initial text-center">
                      Read More
                    </Link>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <ModalApplyPension open={isApplied} onClose={() => setIsApplied(false)} pensionApplies={appliesOn} />
    </>
  )
}
export default DashboardRecommedations
